import { isNullish } from '@adornis/assert/isNullish.js';
import { A } from '@adornis/base/env-info.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { AppliedContract } from '../entities/AppliedContract.js';
import {
  ContractSupplement,
  ContractSupplementChange,
  ContractSupplementDataType,
} from '../entities/ContractSupplement.js';
import { ContractAPI, ContractApplyOptions } from './ContractAPI.js';

export function parseValueToString(value: any): { value: any; type: ContractSupplementDataType } {
  // check against the possible types in NachtragDataType
  if (value === null) {
    return { value: 'null', type: ContractSupplementDataType.NULL };
  }

  switch (typeof value) {
    case 'string':
      return { value, type: ContractSupplementDataType.STRING };
    case 'number':
      return { value: value.toString(), type: ContractSupplementDataType.NUMBER };
    case 'boolean':
      return { value: value === true ? 'true' : 'false', type: ContractSupplementDataType.BOOLEAN };
    case 'object':
      if (value instanceof Date) return { value: value.toISOString(), type: ContractSupplementDataType.DATE };
      return { value: JSON.stringify(value), type: ContractSupplementDataType.JSON };

    default:
      throw new Error('Type not supported', value);
  }
}

/** Dates need to be compared by their ISO representation. Invalid or missing dates map to null so they never equal a valid date. */
function toComparableISO(value: unknown): string | null {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value.toISOString();
  return null;
}

/**
 * This function retrieves all changes between two objects. It returns an array of ContractSupplementChange objects. These changes consist of the path to the changed value, the value itself and the type of the value.
 * @param current the current object
 * @param old the old object as it is saved in the db or applied with all contract supplements
 * @param path the path to begin the recursion. This is used to build the path to the changed value. Do not use this parameter.
 * @returns an array of ContractSupplementChange objects
 */
export function getAllDeepChanges(current: object, old: object, path?: string): ContractSupplementChange[] {
  let changes: ContractSupplementChange[] = [];
  const ignoreKeys = [
    '_id',
    '_class',
    'appliedDate',
    'includesDrafts',
    'createdAt',
    'comment',
    'history',
    'forcedUserAppliedDate',
    'lastAppliedSupplement',
    'appliedSupplementStartAG1',
    'appliedSupplementEndAG1',
    'appliedSupplementStartAG2',
    'appliedSupplementEndAG2',
  ];

  // go trough all keys of the current object
  for (const key in current) {
    // ignore some technical keys which could change depending on the outer selectionset. Such as an nested object is not present and the applied contract generates one.
    if (ignoreKeys.includes(key)) continue;

    const keyIsDate = current[key] instanceof Date;

    const currentPath = path ? `${path}.${key}` : key;
    // if the key is an object and not an array
    if (typeof current[key] === 'object' && !Array.isArray(current[key]) && !keyIsDate && current[key] !== null) {
      changes = changes.concat(getAllDeepChanges(current[key], old[key] ?? {}, currentPath));
    }
    // if the key is an array we need to check if the arrays have the same size. if no we need to change the entire array. else we can change a single value
    else if (Array.isArray(current[key])) {
      // if the array changed in its consistency we need to change the entire array.
      if (current[key]?.length !== old[key]?.length) {
        // array changed from undefined to empty array. Set as new field
        if (current[key]?.length === 0 && old[key] === undefined) {
          changes.push(
            new ContractSupplementChange({
              path: currentPath,
              value: JSON.stringify(current[key]),
              type: ContractSupplementDataType.NEW_FIELD,
            }),
          );
        } else {
          changes.push(
            new ContractSupplementChange({
              path: currentPath,
              value: JSON.stringify(current[key]),
              type: ContractSupplementDataType.JSON,
            }),
          );
        }
      } else {
        // if the array has the same length we need to check each value.
        for (let i = 0; i < current[key].length; i++) {
          const elementIsDate = current[key][i] instanceof Date;

          // dates need to be compared with their ISO representation
          if (elementIsDate) {
            if (toComparableISO(current[key][i]) !== toComparableISO(old[key][i])) {
              const { value, type } = parseValueToString(current[key][i]);
              changes.push(
                new ContractSupplementChange({
                  path: currentPath + '.' + i,
                  value,
                  type,
                }),
              );
            }
          }
          // if the value is an object we need to go deeper
          else if (typeof current[key][i] === 'object' && current[key][i] !== null) {
            changes = changes.concat(getAllDeepChanges(current[key][i], old[key][i], currentPath + '.' + i));
          }
          // if the value is an atomic value we need to check if the value changed
          else if (current[key][i] !== old[key][i]) {
            const { value, type } = parseValueToString(current[key][i]);
            changes.push(
              new ContractSupplementChange({
                path: currentPath + '.' + i,
                value,
                type,
              }),
            );
          }
        }
      }
    }
    // dates need to be compared with their ISO representation
    else if (keyIsDate) {
      if (toComparableISO(current[key]) !== toComparableISO(old[key])) {
        const { value, type } = parseValueToString(current[key]);
        changes.push(
          new ContractSupplementChange({
            path: currentPath,
            value,
            type,
          }),
        );
      }
    }

    // if the value of the current key is different from the old value we need to add a change
    else if ((isNullish(old) && !isNullish(current[key])) || current[key] !== old[key]) {
      const { value, type } = parseValueToString(current[key]);

      // undefined -> null. Field is now available for the first time as the object further up was created.
      if (old[key] === undefined && current[key] === null) {
        changes.push(
          new ContractSupplementChange({
            path: currentPath,
            value,
            type: ContractSupplementDataType.NEW_FIELD,
          }),
        );
      } else {
        changes.push(
          new ContractSupplementChange({
            path: currentPath,
            value,
            type,
          }),
        );
      }
    }
  }

  return changes;
}

export async function generateContractSupplementFromChangedEntity(
  currentContract: AppliedContract,
  object?: EntityData<AppliedContract>,
): Promise<ContractSupplement> {
  // get the next nachtragsnumber by checking the last nachtrag
  const nextNachtragNumber = (currentContract.supplements?.at(-1)?.nachtragsNummer ?? 0) + 1;

  let changes: ContractSupplementChange[] = [];

  // if we are on the client, we can use the doc inside the entity store to retrieve changes. This is only possible on the client
  if (A.isClient) {
    //@ts-ignore doc is private, but we need it here
    changes = getAllDeepChanges(currentContract.toJSON(), object);
  }
  // on the server we need to retrieve the old contract from the db apply the nachträge and then compare it with the current contract.
  else if (A.isServer) {
    const appliedOldContract = await ContractAPI.getApplied(
      currentContract._id,
      new ContractApplyOptions({
        includeDrafts: currentContract.includesDrafts,
        applyDate: currentContract.appliedDate,
      }),
    )(selectionSet(() => AppliedContract, 4));

    if (!appliedOldContract)
      return new ContractSupplement({
        vertragID: currentContract._id,
        nachtragsNummer: nextNachtragNumber,
        changes: [],
      });

    changes = getAllDeepChanges(currentContract.toJSON(), appliedOldContract.toJSON());
  }

  return new ContractSupplement({
    vertragID: currentContract._id,
    nachtragsNummer: nextNachtragNumber,
    changes,
  });
}
